import React, { useState, useEffect, useRef } from 'react';
import {
  Home, Building2, Building, Layers, Trees, Map, Warehouse,
  ChevronDown, Plus, X, RefreshCw, Save,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getContactRentingPreferences,
  updateContactRentingPreferences,
} from '../../../api/contact';
import { SUBURB_REGIONS } from '../../analytics/components/BuyerPreferenceFields';

const PROPERTY_TYPES = [
  { id: 'Apartment', label: 'Apartment', icon: Building2 },
  { id: 'Unit', label: 'Unit', icon: Building2 },
  { id: 'House', label: 'House', icon: Home },
  { id: 'Townhouse', label: 'Townhouse', icon: Building },
  { id: 'Villa', label: 'Villa', icon: Warehouse },
  { id: 'Land', label: 'Land', icon: Trees },
  { id: 'Block of Units', label: 'Block of Units', icon: Layers },
];

const POPULAR_SUBURBS = [
  'Northcote', 'Brunswick', 'Fitzroy', 'Richmond', 'South Yarra', 'Preston',
  'Camberwell', 'Brighton', 'Footscray', 'Parramatta', 'Bondi', 'Chatswood',
];

const BED_OPTIONS = ['Any', '1', '2', '3', '4', '5', '6+'];
const BATH_OPTIONS = ['Any', '1+', '2+', '3+', '4+', '5+'];
const CAR_OPTIONS = ['Any', '0', '1', '2', '3', '4+'];
const MOVE_IN_OPTIONS = ['Any', 'ASAP', '0-1 month', '1-3 months', '3-6 months', '6+ months'];
const PETS_OPTIONS = ['Any', 'None', 'Dog', 'Cat', 'Dog & Cat', 'Other'];
const OCCUPANTS_OPTIONS = ['Any', '1', '2', '3', '4', '5', '6+'];

// Weekly rent, unlike BuyingPreference's sale-price steps.
const RENT_MIN_OPTIONS = ['Any', '$200', '$300', '$400', '$500', '$600', '$700', '$800', '$900', '$1,000', '$1,200'];
const RENT_MAX_OPTIONS = ['Any', '$300', '$400', '$500', '$600', '$700', '$800', '$900', '$1,000', '$1,200', '$1,500', '$2,000+'];

const toTitleCase = (str) =>
  (str || '').toLowerCase().split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const formatMoney = (val) => {
  if (val === null || val === undefined) return 'Any';
  const s = String(val).trim();
  if (!s || s.toLowerCase() === 'any') return 'Any';
  if (s.startsWith('$')) return s;
  const digits = s.replace(/[^\d.]/g, '');
  if (!digits) return s;
  const n = Math.round(parseFloat(digits));
  if (!Number.isFinite(n)) return s;
  return `$${n.toLocaleString('en-AU')}`;
};

const toPlusOption = (val, options) => {
  const s = (val === null || val === undefined) ? '' : String(val).trim();
  if (!s || s.toLowerCase() === 'any') return 'Any';
  if (options.includes(s)) return s;
  if (/^\d+$/.test(s) && options.includes(`${s}+`)) return `${s}+`;
  return s;
};

const AU_STATE_SUFFIXES = [
  'Australian Capital Territory', 'New South Wales', 'Northern Territory',
  'South Australia', 'Western Australia', 'Queensland', 'Tasmania', 'Victoria',
  'ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA',
];

const stripStateSuffix = (val) => {
  const s = (val || '').trim();
  if (!s) return '';
  for (const st of AU_STATE_SUFFIXES) {
    const re = new RegExp(`[\\s,]+${st}$`, 'i');
    if (re.test(s)) {
      const candidate = s.replace(re, '').trim();
      if (candidate) return candidate;
    }
  }
  return s;
};

const buildSuburbSuggestions = (q) => {
  const query = (q || '').trim().toLowerCase();
  if (query.length < 2) return [];
  const seen = new Set();
  const matches = [];
  for (const r of SUBURB_REGIONS) {
    if (r.name.toLowerCase().includes(query) && !seen.has(r.name.toLowerCase())) {
      seen.add(r.name.toLowerCase());
      matches.push(r);
    }
  }
  matches.sort((a, b) => {
    const ax = a.name.toLowerCase() === query ? 0 : 1;
    const bx = b.name.toLowerCase() === query ? 0 : 1;
    return ax - bx;
  });
  const out = [];
  if (!seen.has(query)) out.push({ name: toTitleCase(query), type: 'manual', isManual: true });
  out.push(...matches);
  return out.slice(0, 8);
};

const cleanSuburbs = (list) => {
  const seen = new Set();
  const out = [];
  for (const raw of (list || [])) {
    const s = stripStateSuffix(raw);
    if (s && !seen.has(s.toLowerCase())) {
      seen.add(s.toLowerCase());
      out.push(s);
    }
  }
  return out;
};

const EMPTY = {
  propertyType: [],
  rentMin: 'Any',
  rentMax: 'Any',
  bedroomsMin: 'Any',
  bedroomsMax: 'Any',
  bathrooms: 'Any',
  bathroomsMax: 'Any',
  carSpaces: 'Any',
  carSpacesMax: 'Any',
  suburbs: [],
  secondarySuburbs: [],
  moveInTimeframe: 'Any',
  moveInDate: '',
  pets: 'Any',
  occupants: 'Any',
};

const mapToFrontend = (data) => {
  if (!data || Object.keys(data).length === 0) return { ...EMPTY };
  let propertyTypeArray = [];
  if (Array.isArray(data.property_type)) propertyTypeArray = data.property_type;
  else if (data.property_type && data.property_type !== 'Any') propertyTypeArray = [data.property_type];
  return {
    propertyType: propertyTypeArray,
    rentMin: formatMoney(data.rent_min),
    rentMax: formatMoney(data.rent_max),
    bedroomsMin: data.bedrooms_min || 'Any',
    bedroomsMax: data.bedrooms_max || 'Any',
    bathrooms: toPlusOption(data.bathrooms, BATH_OPTIONS),
    bathroomsMax: toPlusOption(data.bathrooms_max, BATH_OPTIONS),
    carSpaces: data.car_spaces || 'Any',
    carSpacesMax: data.car_spaces_max || 'Any',
    suburbs: cleanSuburbs(data.suburbs),
    secondarySuburbs: cleanSuburbs(data.secondary_suburbs),
    moveInTimeframe: data.move_in_timeframe || 'Any',
    moveInDate: data.move_in_date || '',
    pets: data.pets || 'Any',
    occupants: data.occupants || 'Any',
  };
};

const mapToBackend = (data) => ({
  property_type: data.propertyType,
  rent_min: data.rentMin,
  rent_max: data.rentMax,
  bedrooms_min: data.bedroomsMin,
  bedrooms_max: data.bedroomsMax,
  bathrooms: data.bathrooms,
  bathrooms_max: data.bathroomsMax,
  car_spaces: data.carSpaces,
  car_spaces_max: data.carSpacesMax,
  suburbs: data.suburbs,
  secondary_suburbs: data.secondarySuburbs,
  move_in_timeframe: data.moveInTimeframe,
  // DateField rejects '' - send null when no date is set.
  move_in_date: data.moveInDate || null,
  pets: data.pets,
  occupants: data.occupants,
});

const Dropdown = ({ name, value, options, onChange }) => {
  const opts = (value && value !== 'Any' && !options.includes(value))
    ? [value, ...options]
    : options;
  return (
  <div className="relative">
    <select
      name={name}
      value={value}
      onChange={onChange}
      className="w-full h-[48px] px-4 rounded-xl border border-gray-300 bg-white text-[14px] font-medium appearance-none cursor-pointer focus:outline-none focus:border-[#55C5D0] focus:ring-2 focus:ring-[#55C5D0]/10 transition-all"
    >
      {opts.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
    </select>
    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
  </div>
  );
};

const Chip = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={selected}
    className={`px-3 sm:px-4 py-2 rounded-lg text-[12px] sm:text-[13px] font-bold transition-all ${
      selected
        ? 'bg-[#E0F7FA] text-[#00838F] border border-[#55C5D0] cursor-default'
        : 'bg-white border border-gray-300 text-gray-600 hover:border-[#55C5D0] hover:bg-[#F0FDFE]'
    }`}
  >
    {label}
  </button>
);

/**
 * Compact suburb multi-select (input + suggestions + chips), used for the
 * Secondary Suburbs field. Self-contained state so it doesn't touch the primary
 * suburb picker's behaviour.
 */
const SuburbBox = ({ suburbs, onAdd, onRemove, placeholder }) => {
  const [input, setInput] = useState('');
  const [sugg, setSugg] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const onType = (v) => { setInput(v); const s = buildSuburbSuggestions(v); setSugg(s); setOpen(s.length > 0); };
  const add = (raw) => {
    const val = stripStateSuffix(toTitleCase((raw ?? input).trim()));
    if (val) onAdd(val);
    setInput(''); setSugg([]); setOpen(false);
  };

  return (
    <div className="space-y-3">
      <div className="relative" ref={boxRef}>
        <div className="flex items-stretch gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => onType(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            onFocus={() => sugg.length > 0 && setOpen(true)}
            placeholder={placeholder || 'Search for a suburb...'}
            autoComplete="off"
            className="flex-1 min-w-0 h-[48px] px-4 rounded-xl border border-gray-300 bg-white text-[14px] font-medium placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/10 focus:border-[#55C5D0] transition-all"
          />
          <button
            type="button"
            onClick={() => add()}
            className="shrink-0 bg-[#55C5D0] hover:bg-[#45B5C0] text-white h-[48px] px-4 sm:px-6 rounded-xl font-bold text-[14px] transition-all flex items-center gap-1.5 shadow-sm"
          >
            <Plus size={18} /> Add
          </button>
        </div>
        {open && sugg.length > 0 && (
          <div className="absolute z-30 w-full mt-1.5 bg-white rounded-xl border border-gray-200 shadow-xl max-h-60 overflow-y-auto">
            {sugg.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => add(s.name)}
                className="w-full text-left px-4 py-2.5 hover:bg-[#F0FDFE] border-b border-gray-100 last:border-0 flex items-center gap-2.5"
              >
                <span className="p-1 bg-gray-100 rounded">
                  {s.isManual ? <Plus className="w-3 h-3 text-[#55C5D0]" /> : <Map className="w-3 h-3 text-gray-500" />}
                </span>
                <span className="text-[13px] text-gray-800 font-medium">
                  {s.isManual ? `Add "${s.name}"` : s.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      {suburbs.length > 0 && (
        <div className="flex flex-wrap gap-2.5">
          {suburbs.map((s, i) => (
            <div key={i} className="inline-flex items-center gap-2.5 px-4 py-2 bg-[#EAF6F7] text-black text-[13px] font-bold rounded-lg border border-[#9BD9DF]">
              {s}
              <button type="button" onClick={() => onRemove(s)} className="hover:scale-110 transition-transform">
                <X size={14} strokeWidth={3} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Agent-side Renting Preferences editor - the rental-side counterpart to
 * ContactBuyerPreferencesEditor, backed by RentingPreference (not
 * BuyingPreference) so renter demand is tracked as its own analytics.
 */
const ContactRentingPreferencesEditor = ({ contactId, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [portalUserFound, setPortalUserFound] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [suburbInput, setSuburbInput] = useState('');
  const [suburbSuggestions, setSuburbSuggestions] = useState([]);
  const [suburbOpen, setSuburbOpen] = useState(false);
  const suburbBoxRef = useRef(null);

  useEffect(() => {
    const away = (e) => {
      if (suburbBoxRef.current && !suburbBoxRef.current.contains(e.target)) setSuburbOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const res = await getContactRentingPreferences(contactId);
        if (!active) return;
        setPortalUserFound(!!res.data?.portal_user_found);
        setForm(mapToFrontend(res.data?.preference));
      } catch (e) {
        console.error('Failed to load renting preferences:', e);
        if (active) setForm({ ...EMPTY });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [contactId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const togglePropertyType = (id) =>
    setForm((prev) => ({
      ...prev,
      propertyType: prev.propertyType.includes(id)
        ? prev.propertyType.filter((v) => v !== id)
        : [...prev.propertyType, id],
    }));

  const addSuburb = (raw) => {
    const val = stripStateSuffix(toTitleCase((raw ?? suburbInput).trim()));
    if (val && !form.suburbs.some((s) => s.toLowerCase() === val.toLowerCase())) {
      setForm((prev) => ({ ...prev, suburbs: [...prev.suburbs, val] }));
    }
    setSuburbInput('');
    setSuburbSuggestions([]);
    setSuburbOpen(false);
  };

  const onSuburbType = (v) => {
    setSuburbInput(v);
    const s = buildSuburbSuggestions(v);
    setSuburbSuggestions(s);
    setSuburbOpen(s.length > 0);
  };

  const removeSuburb = (s) =>
    setForm((prev) => ({ ...prev, suburbs: prev.suburbs.filter((x) => x !== s) }));

  const addSecondarySuburb = (val) =>
    setForm((prev) => (
      prev.secondarySuburbs.some((s) => s.toLowerCase() === val.toLowerCase())
        ? prev
        : { ...prev, secondarySuburbs: [...prev.secondarySuburbs, val] }
    ));

  const removeSecondarySuburb = (s) =>
    setForm((prev) => ({ ...prev, secondarySuburbs: prev.secondarySuburbs.filter((x) => x !== s) }));

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await updateContactRentingPreferences(contactId, mapToBackend(form));
      if (res.data?.preference) setForm(mapToFrontend(res.data.preference));
      toast.success('Renter preferences saved');
      onSaved?.(res.data?.preference);
    } catch (e) {
      console.error('Failed to save renting preferences:', e);
      toast.error('Failed to save renter preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#55C5D0]"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 space-y-8">
      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
        <div className={`w-3 h-3 rounded-full ${portalUserFound ? 'bg-green-500' : 'bg-gray-300'}`}></div>
        <span className="text-sm font-bold text-gray-700">
          {portalUserFound ? 'Verified Portal Account Connected' : 'No Portal Account Matched'}
        </span>
        <span className="ml-auto text-xs text-gray-400">Tracked separately from buyer preferences</span>
      </div>

      <div className="space-y-3">
        <label className="text-[16px] font-bold text-gray-900 block">Property Type</label>
        <p className="text-[12px] text-gray-500 -mt-2">Select one or more property types</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {PROPERTY_TYPES.map((type) => {
            const isSelected = form.propertyType.includes(type.id);
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => togglePropertyType(type.id)}
                className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border transition-all w-full hover:shadow-md ${
                  isSelected
                    ? 'bg-[#E0F7FA] border-[#55C5D0] shadow-sm'
                    : 'bg-white border-gray-300 hover:border-[#55C5D0]/50 hover:bg-gray-50'
                }`}
              >
                <div className={`p-1.5 rounded-full ${isSelected ? 'bg-[#55C5D0]/20 text-[#00838F]' : 'bg-gray-100 text-gray-500'}`}>
                  <Icon size={18} />
                </div>
                <span className={`text-[13px] font-bold ${isSelected ? 'text-[#00838F]' : 'text-gray-700'}`}>{type.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <hr className="border-gray-100" />

      <div className="space-y-5">
        <div>
          <label className="text-[18px] font-bold text-gray-900 block">Preferred Suburbs</label>
          <p className="text-[13px] text-gray-500 font-medium mt-1">Add specific suburbs for the best rental matches.</p>
        </div>

        <div className="relative" ref={suburbBoxRef}>
          <div className="flex items-stretch gap-2">
            <input
              type="text"
              value={suburbInput}
              onChange={(e) => onSuburbType(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSuburb(); } }}
              onFocus={() => suburbSuggestions.length > 0 && setSuburbOpen(true)}
              placeholder="Search for a specific suburb..."
              autoComplete="off"
              className="flex-1 min-w-0 h-[48px] px-4 rounded-xl border border-gray-300 bg-white text-[14px] font-medium placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/10 focus:border-[#55C5D0] transition-all"
            />
            <button
              type="button"
              onClick={() => addSuburb()}
              className="shrink-0 bg-[#55C5D0] hover:bg-[#45B5C0] text-white h-[48px] px-4 sm:px-6 rounded-xl font-bold text-[14px] transition-all flex items-center gap-1.5 shadow-sm"
            >
              <Plus size={18} /> Add
            </button>
          </div>
          {suburbOpen && suburbSuggestions.length > 0 && (
            <div className="absolute z-30 w-full mt-1.5 bg-white rounded-xl border border-gray-200 shadow-xl max-h-60 overflow-y-auto">
              {suburbSuggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => addSuburb(s.name)}
                  className="w-full text-left px-4 py-2.5 hover:bg-[#F0FDFE] border-b border-gray-100 last:border-0 flex items-center gap-2.5"
                >
                  <span className="p-1 bg-gray-100 rounded">
                    {s.isManual ? <Plus className="w-3 h-3 text-[#55C5D0]" /> : <Map className="w-3 h-3 text-gray-500" />}
                  </span>
                  <span className="text-[13px] text-gray-800 font-medium">
                    {s.isManual ? `Add "${s.name}"` : s.name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-[13px] font-bold text-gray-500">Popular Suburbs</p>
          <div className="flex flex-wrap gap-2">
            {POPULAR_SUBURBS.map((s) => (
              <Chip key={s} label={s} selected={form.suburbs.includes(s)} onClick={() => addSuburb(s)} />
            ))}
          </div>
        </div>

        {form.suburbs.length > 0 && (
          <div className="flex flex-wrap gap-2.5">
            {form.suburbs.map((s, i) => (
              <div key={i} className="inline-flex items-center gap-2.5 px-4 py-2 bg-[#C9F9FF] text-black text-[13px] font-bold rounded-lg border border-[#55C5D0]">
                {s}
                <button type="button" onClick={() => removeSuburb(s)} className="hover:scale-110 transition-transform">
                  <X size={14} strokeWidth={3} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <hr className="border-gray-100" />

      <div className="space-y-3">
        <div>
          <label className="text-[16px] font-bold text-gray-900 block">Secondary Suburbs</label>
          <p className="text-[13px] text-gray-500 font-medium mt-1">Suburbs they would also consider (kept separate from their preferred list).</p>
        </div>
        <SuburbBox
          suburbs={form.secondarySuburbs}
          onAdd={addSecondarySuburb}
          onRemove={removeSecondarySuburb}
          placeholder="Add a backup suburb..."
        />
      </div>

      <hr className="border-gray-100" />

      <div className="space-y-4">
        <h3 className="text-[18px] font-bold text-gray-900">Weekly Rent</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Min</p>
            <Dropdown name="rentMin" value={form.rentMin} options={RENT_MIN_OPTIONS} onChange={handleChange} />
          </div>
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Max</p>
            <Dropdown name="rentMax" value={form.rentMax} options={RENT_MAX_OPTIONS} onChange={handleChange} />
          </div>
        </div>
      </div>

      <hr className="border-gray-100" />

      <div className="space-y-4">
        <h3 className="text-[18px] font-bold text-gray-900">Bedrooms</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Min</p>
            <Dropdown name="bedroomsMin" value={form.bedroomsMin} options={BED_OPTIONS} onChange={handleChange} />
          </div>
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Max</p>
            <Dropdown name="bedroomsMax" value={form.bedroomsMax} options={BED_OPTIONS} onChange={handleChange} />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-[18px] font-bold text-gray-900">Bathrooms</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Min</p>
            <Dropdown name="bathrooms" value={form.bathrooms} options={BATH_OPTIONS} onChange={handleChange} />
          </div>
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Max</p>
            <Dropdown name="bathroomsMax" value={form.bathroomsMax} options={BATH_OPTIONS} onChange={handleChange} />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-[18px] font-bold text-gray-900">Car Spaces</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Min</p>
            <Dropdown name="carSpaces" value={form.carSpaces} options={CAR_OPTIONS} onChange={handleChange} />
          </div>
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Max</p>
            <Dropdown name="carSpacesMax" value={form.carSpacesMax} options={CAR_OPTIONS} onChange={handleChange} />
          </div>
        </div>
      </div>

      <hr className="border-gray-100" />

      <div className="space-y-4">
        <h3 className="text-[18px] font-bold text-gray-900">Move-in</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Timeframe</p>
            <Dropdown name="moveInTimeframe" value={form.moveInTimeframe} options={MOVE_IN_OPTIONS} onChange={handleChange} />
          </div>
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Preferred date</p>
            <input
              type="date"
              name="moveInDate"
              value={form.moveInDate || ''}
              onChange={handleChange}
              className="w-full h-[48px] px-4 rounded-xl border border-gray-300 bg-white text-[14px] font-medium focus:outline-none focus:border-[#55C5D0] focus:ring-2 focus:ring-[#55C5D0]/10 transition-all"
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-[18px] font-bold text-gray-900">Household</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Pets</p>
            <Dropdown name="pets" value={form.pets} options={PETS_OPTIONS} onChange={handleChange} />
          </div>
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Occupants</p>
            <Dropdown name="occupants" value={form.occupants} options={OCCUPANTS_OPTIONS} onChange={handleChange} />
          </div>
        </div>
      </div>

      <div className="pt-2 flex justify-end gap-3 sticky bottom-0 bg-white py-4 -mx-4 px-4 sm:-mx-8 sm:px-8 border-t border-gray-100">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-[#55C5D0] text-white rounded-xl text-sm font-bold shadow-md hover:bg-[#45B5C0] flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Preferences
        </button>
      </div>
    </div>
  );
};

export default ContactRentingPreferencesEditor;
